// components/EventDetail/EventDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  IndianRupee, 
  Users, 
  Clock, 
  ArrowLeft,
  Trophy,
  Award,
  Medal,
  Mail,
  Phone,
  User,
  CheckCircle,
  ExternalLink,
  Gift,
  Star,
  Image as ImageIcon
} from 'lucide-react';
import { eventsData } from './../EventPage/eventsData';

const EventDetail = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<any>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Find the event in eventsData
    const allEvents = [
      ...eventsData.day1.flagship,
      ...eventsData.day1.technical,
      ...eventsData.day1['non-technical'],
      ...eventsData.day1.games,
      ...eventsData.day2.flagship,
      ...eventsData.day2.technical,
      ...eventsData.day2['non-technical'],
      ...eventsData.day2.games,
    ];

    const foundEvent = allEvents.find(e => {
      const id = e.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      return id === eventId;
    });

    if (foundEvent) {
      setEvent(foundEvent);
      setImageError(false);
    }
  }, [eventId]);

  const getDefaultImage = (eventType: string) => {
    switch(eventType) {
      case 'technical':
        return 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
      case 'non-technical':
        return 'https://images.unsplash.com/photo-1593305841991-05c297ba4575?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
      case 'games':
        return 'https://images.unsplash.com/photo-1542751371-adc38448a05e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
      case 'flagship':
        return 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
      default:
        return 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
    }
  };

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white p-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-red-500 hover:text-red-400 mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Events
          </button>
          <div className="text-center py-20">
            <h1 className="text-4xl font-bold mb-4">Event Not Found</h1>
            <p className="text-gray-300 mb-8">The event you're looking for doesn't exist or has been moved.</p>
            <button
              onClick={() => navigate('/events')}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 rounded-lg hover:from-red-500 hover:to-red-600 transition-all"
            >
              Browse All Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  const imageSrc = imageError 
    ? getDefaultImage(event.category || 'technical')
    : event.imageUrl || getDefaultImage(event.category || 'technical');

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <br /><br />
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-red-500 hover:text-red-400 mb-8 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          Back to Events
        </button>

        {/* Event Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
            {event.name}
          </h1>
          <p className="text-gray-300 text-lg md:text-xl">{event.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Image */}
            <div className="rounded-2xl overflow-hidden border border-gray-800 relative">
              <div className="h-64 md:h-96 bg-gradient-to-br from-red-900/30 via-black/50 to-red-900/20 relative">
                {imageSrc ? (
                  <>
                    <img 
                      src={imageSrc}
                      alt={event.name}
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-500">Event Image</p>
                    </div>
                  </div>
                )}
                
                {/* Event name overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{event.name}</h2>
                </div>
              </div>
            </div>

            {/* Event Description */}
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
              <h2 className="text-2xl font-bold mb-6 text-white">About this Event</h2>
              <p className="text-gray-300 leading-relaxed text-lg">
                {event.detailedDescription || event.description}
              </p>
            </div>

            {/* Prize Section with Divider */}
            {event.prizes && (
              <div className="bg-gradient-to-br from-red-900/20 to-black/50 rounded-2xl p-6 border border-red-800/30">
                <div className="flex items-center gap-3 mb-6">
                  <Trophy className="text-yellow-500" size={28} />
                  <h2 className="text-2xl font-bold text-white">Prizes & Awards</h2>
                </div>
                
                {/* Prize Divider */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-red-500/20"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-gradient-to-b from-red-900/20 to-black text-red-400 font-bold text-sm tracking-wider">
                      WINNING REWARDS
                    </span>
                  </div>
                </div>

                {/* Prize Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* First Prize */}
                  {event.prizes.first && (
                    <div className="relative bg-gradient-to-br from-yellow-900/20 to-black/50 rounded-xl p-5 border border-yellow-600/30 group hover:border-yellow-500/50 transition-colors">
                      <div className="absolute -top-3 -right-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-yellow-600 to-yellow-800 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-yellow-200 font-bold">1</span>
                        </div>
                      </div>
                      <div className="text-center pt-3">
                        <Trophy className="w-10 h-10 text-yellow-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                        <p className="text-sm text-gray-400 mb-1">First Prize</p>
                        <p className="text-2xl font-bold text-yellow-400">{event.prizes.first}</p>
                      </div>
                    </div>
                  )}

                  {/* Second Prize */}
                  {event.prizes.second && (
                    <div className="relative bg-gradient-to-br from-gray-800/20 to-black/50 rounded-xl p-5 border border-gray-700/30 group hover:border-gray-600/50 transition-colors">
                      <div className="absolute -top-3 -right-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-800 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-gray-300 font-bold">2</span>
                        </div>
                      </div>
                      <div className="text-center pt-3">
                        <Award className="w-10 h-10 text-gray-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                        <p className="text-sm text-gray-400 mb-1">Second Prize</p>
                        <p className="text-2xl font-bold text-gray-300">{event.prizes.second}</p>
                      </div>
                    </div>
                  )}

                  {/* Third Prize */}
                  {event.prizes.third && (
                    <div className="relative bg-gradient-to-br from-amber-900/20 to-black/50 rounded-xl p-5 border border-amber-700/30 group hover:border-amber-600/50 transition-colors">
                      <div className="absolute -top-3 -right-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-700 to-amber-900 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <span className="text-amber-300 font-bold">3</span>
                        </div>
                      </div>
                      <div className="text-center pt-3">
                        <Medal className="w-10 h-10 text-amber-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                        <p className="text-sm text-gray-400 mb-1">Third Prize</p>
                        <p className="text-2xl font-bold text-amber-400">{event.prizes.third}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Other Prizes */}
                {event.prizes.others && event.prizes.others.length > 0 && (
                  <div className="bg-black/30 rounded-xl p-5 border border-gray-800/50">
                    <div className="flex items-center gap-3 mb-4">
                      <Gift className="text-red-400" size={24} />
                      <h3 className="text-lg font-semibold text-white">Additional Rewards</h3>
                    </div>
                    <ul className="space-y-2">
                      {event.prizes.others.map((prize: string, index: number) => (
                        <li key={index} className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                          <Star className="text-red-500" size={16} />
                          <span>{prize}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Rules & Guidelines */}
            {event.rules && event.rules.length > 0 && (
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                <h2 className="text-2xl font-bold mb-6 text-white">Rules & Guidelines</h2>
                <ul className="space-y-4">
                  {event.rules.map((rule: string, index: number) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <CheckCircle className="text-red-500 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" size={20} />
                      <span className="text-gray-300 group-hover:text-gray-200 transition-colors">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Requirements */}
            {event.requirements && event.requirements.length > 0 && (
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                <h2 className="text-2xl font-bold mb-6 text-white">Requirements</h2>
                <ul className="space-y-4">
                  {event.requirements.map((req: string, index: number) => (
                    <li key={index} className="flex items-center gap-3 group">
                      <div className="w-2 h-2 bg-red-500 rounded-full group-hover:scale-125 transition-transform" />
                      <span className="text-gray-300 group-hover:text-gray-200 transition-colors">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Event Details Card */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                <h3 className="text-xl font-bold mb-6 text-white">Event Details</h3>
                
                <div className="space-y-5">
                  {/* Date */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <Calendar className="text-red-400" size={22} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Date & Time</p>
                      <p className="font-medium text-white">{event.date || "To be announced"}</p>
                    </div>
                  </div>

                  {/* Venue */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-red-400" size={22} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Venue</p>
                      <p className="font-medium text-white">{event.venue || "Main Auditorium"}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <IndianRupee className="text-red-400" size={22} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Registration Fee</p>
                      <p className="font-medium text-white">
                        {event.price === 0 ? "FREE Entry" : `₹${event.price}`}
                      </p>
                    </div>
                  </div>

                  {/* Team Size */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <Users className="text-red-400" size={22} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Team Size</p>
                      <p className="font-medium text-white">{event.teamSize || "Individual"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Register Button */}
              <button 
                onClick={() => window.open(event.registrationLink ,'_blank')}
                className="w-full py-4 px-6 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-red-500/30 flex items-center justify-center gap-3 group"
              >
                <span className="text-lg">Register Now</span>
                <ExternalLink size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Contact Information */}
              {event.contact && (
                <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
                  <h3 className="text-xl font-bold mb-6 text-white">Contact Information</h3>
                  <div className="space-y-4">
                    {event.contact.email && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-900/20 flex items-center justify-center">
                          <Mail className="text-red-400" size={18} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Email</p>
                          <a href={`mailto:${event.contact.email}`} className="text-red-400 hover:text-red-300 transition-colors">
                            {event.contact.email}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {event.contact.phone && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-900/20 flex items-center justify-center">
                          <Phone className="text-red-400" size={18} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Phone</p>
                          <a href={`tel:${event.contact.phone}`} className="text-red-400 hover:text-red-300 transition-colors">
                            {event.contact.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {event.contact.coordinator && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-900/20 flex items-center justify-center">
                          <User className="text-red-400" size={18} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Coordinator</p>
                          <p className="text-white">{event.contact.coordinator}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;